import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setGlobalLoading,
  setGlobalUserDetails,
  setGlobalUserInfo,
  setGlobalFeatureFlags,
  setToken,
} from "scenes/redux";
import { UserDetails, UserInfo } from "types/UserInfo";
import { useAuth } from "react-oidc-context";
import storage from "../persistance";
import { selectUserDetails, selectUserInfo } from "./selectors";
import useLogout from "./useLogout";

export const useAuthentication = (props: { route: string }) => {
  const emptyUserInfo = {
    sub: "",
    cidpId: "",
    email: "",
    ext: {
      sub_type: "",
      auth_provider: "",
    },
    given_name: "",
    family_name: "",
    preferred_username: "",
  };

  const emptyUserDetails: UserDetails = {
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    directLoginEnabled: false,
    identities: [
      {
        partnerId: "",
        partnerType: "",
        partnerName: "",
      },
    ],
    cidpId: "",
  };

  const auth = useAuth();

  const [jwtToken, setJwtToken] = useState<string>("");
  const [featureFlags, setFeatureFlags] = useState<Array<string>>([]);

  const dispatch = useDispatch();
  const logout = useLogout();

  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === "logout-event") {
        // Another tab requested logout
        storage.clear();
        auth.revokeTokens();
        window.location.href = `${window.location.origin}/signedOut`;
      }
    };

    window.addEventListener("storage", handleStorageEvent);
    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [auth]);

  const storedUserInfo = useSelector(selectUserInfo);
  const storedUserDetail = useSelector(selectUserDetails);

  const fetchUserInfo = async (
    token: string
  ): Promise<{ userInformation: UserInfo; userDetails: UserDetails }> => {
    if (token === "") {
      return { userInformation: emptyUserInfo, userDetails: emptyUserDetails };
    }

    // If userinfo already exists, return it in a promise instead of calling again.
    if (storedUserInfo.email !== "") {
      return Promise.resolve({
        userInformation: storedUserInfo,
        userDetails: storedUserDetail,
      });
    }

    const url = `${
      window?.config?.API_URL || "https://dev.envestnet.io"
    }/oauth2/v2/userinfo`;

    const userInfoResponse = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const userInformation: UserInfo = await userInfoResponse.json();

    const userDetailUrl = `${window.config.API_URL}/ciam/ext/users/${userInformation.cidpId}`;

    const userDetailsResponse = await fetch(userDetailUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const userDetailsData: UserDetails = await userDetailsResponse.json();

    return { userInformation, userDetails: userDetailsData };
  };

  const fetchFeatureFlags = async (token: string): Promise<Array<string>> => {
    const flagsUrl = `${window.config.API_URL}/ciam/ext/support/features`;
    const flagsResponse = await fetch(flagsUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const flagsData = await flagsResponse.json();
    return flagsData;
  };

  useEffect(() => {
    const checkAuthState = async () => {
      dispatch(setGlobalLoading(true));
      if (auth && auth.activeNavigator !== "signoutRedirect") {
        if (!auth.isAuthenticated && !auth.isLoading) {
          const params = new URLSearchParams(window.location.search);
          const resourceId = params.get("resourceId") || "empty";

          await auth.signinRedirect({
            redirect_uri: props.route,
            state: resourceId,
          });
        }

        if (auth.isAuthenticated) {
          const accessToken = auth.user?.access_token ?? "";
          dispatch(setToken(accessToken));
          setJwtToken(accessToken);
        }
      }
    };
    checkAuthState();
  }, [auth.isAuthenticated, auth.isLoading]);

  useEffect(() => {
    const getUserInfo = async () => {
      if (jwtToken) {
        const userInformationData = await fetchUserInfo(jwtToken);
        dispatch(setGlobalUserInfo(userInformationData.userInformation));
        dispatch(setGlobalUserDetails(userInformationData.userDetails));
        dispatch(setGlobalLoading(false));

        const featureFlagData: Array<string> = await fetchFeatureFlags(
          jwtToken
        );
        dispatch(setGlobalFeatureFlags(featureFlagData));
        setFeatureFlags(featureFlagData);
      }
    };
    getUserInfo();
  }, [jwtToken]);

  // stored selectors reflect live Redux state, so they pick up updates like username/detail changes.
  return [storedUserInfo, logout, storedUserDetail, featureFlags];
};
// set user details in global redux and also as part of the useState.
// Update withAuth to send directLoginEnabled variable as prop to all scenes
